import React, { useEffect, useState } from 'react';
import { Box, useTheme, Icons, IconButton } from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomPostCard from './CommonRoomPostCard.container';
import useBreakpoints from '@app/hooks/useBreakpoints';
import CommonRoomTabs, { CommonRoomTab } from './CommonRoomTabs';
import type { CreatePostAuthor } from '../Shared/ComposePost/ComposePost';
import ComposePost from './ComposePost.container';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import StudentAction from './StudentAction/StudentAction';
import pushRoute from '@app/utils/routing';
import { getCommonRoomPath } from '@app/actions/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import ScheduledPostsSummaryContainer from './ScheduledPostsSummary';

interface CommonRoomFeedResponsiveProps {
  posts?: Post[];
  readOnly?: boolean;
  pinnedCount?: number;
  author?: CreatePostAuthor;
  onTabChange?: (tab: CommonRoomTab) => void;
}

const CommonRoomFeedResponsive = ({
  posts = [],
  author,
  readOnly = false,
  pinnedCount,
  onTabChange,
}: CommonRoomFeedResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { md } = useBreakpoints();
  const [activeTab, setActiveTab] = useState<CommonRoomTab>('feed');
  const spaceId = useSelector(getCurrentSpaceId);
  const isStaffAccess = useSelector(accessingCurrentSpaceAsStaff);

  useEffect(() => {
    if (md) {
      setActiveTab('feed');
    }
  }, [md]);

  const handleTabChange = (tab: CommonRoomTab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const isPinnedSelected = activeTab === 'pinned';
  const commonRoomPath = getCommonRoomPath(spaceId);
  // handle compose post mobile
  const handleComposePostMobile = () => {
    pushRoute(`${commonRoomPath}/new`);
  };

  return (
    <Box sx={styles.feedLayout} data-testid="responsive-feed-layout">
      {md && !isStaffAccess && (
        <Box
          component="section"
          aria-label="Post composer"
          sx={styles.composerRegion}
        >
          <ComposePost feedType={COMMON_ROOM} />
        </Box>
      )}
      {!md && (
        <Box
          component="section"
          aria-label="Feed controls"
          sx={styles.feedControlsRegion}
        >
          <CommonRoomTabs
            activeTab={activeTab}
            pinnedCount={pinnedCount}
            onTabChange={handleTabChange}
          />
          {!isStaffAccess && (
            <IconButton
              aria-label="Compose post"
              onClick={handleComposePostMobile}
              sx={styles.createPostButton}
            >
              <Icons.DriveFileRenameOutline />
            </IconButton>
          )}
        </Box>
      )}
      <Box
        component="section"
        aria-label={
          isPinnedSelected ? 'Pinned Common Room posts' : 'Common Room feed'
        }
        sx={styles.postsRegion}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          {!isStaffAccess && <StudentAction theme={theme} />}
        </Box>
        <Box sx={styles.postsList}>
          <ScheduledPostsSummaryContainer />
          {posts?.map((post) => (
            <CommonRoomPostCard
              key={post.objectId}
              post={post}
              readOnly={readOnly}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CommonRoomFeedResponsive;
